import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Plus, X, Send } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Org-level email alerts for scheduled scans (Resend): toggle, extra recipients, test send. */
export default function EmailAlertsCard() {
  const { isAdmin, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => authApi.org.alerts().then(setInfo).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (patch, ok) => {
    setBusy(true);
    try {
      await authApi.org.patch(patch);
      await Promise.all([load(), refresh()]);
      toast.success(ok);
    } catch (err) {
      toast.error("Could not update alerts", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  const add = (e) => {
    e.preventDefault();
    const email = draft.trim().toLowerCase();
    if (!EMAIL.test(email)) return toast.error("Enter a valid email address");
    if (info.alert_recipients.includes(email)) return toast.info("Already on the list");
    setDraft("");
    return save({ alert_recipients: [...info.alert_recipients, email] }, `${email} added`);
  };
  const test = async () => {
    setTesting(true);
    try {
      const res = await authApi.org.testAlert();
      toast.success("Test alert sent", { description: `Delivered to ${res.to.join(", ")} - check your inbox.` });
    } catch (err) {
      toast.error("Test email failed", { description: errorMessage(err) });
    } finally {
      setTesting(false);
    }
  };

  if (!info) return null;
  return (
    <section className="ws-card glass" data-testid="email-alerts-card">
      <h2>
        <Mail size={16} /> Email alerts
      </h2>
      <p className="muted small">
        When a scheduled scan (or “Check for changes”) finds seat, user, gap or policy changes - or fails - an email goes to every org admin plus the recipients below.{" "}
        {info.configured ? (
          <>
            Sent from <span className="mono">{info.sender}</span>.
          </>
        ) : (
          <span className="warn">Email delivery is not configured on the server.</span>
        )}
      </p>
      {isAdmin ? (
        <>
          <label className="ws-switch" data-testid="email-alerts-toggle">
            <Switch checked={info.email_alerts !== false} onCheckedChange={(v) => save({ email_alerts: v }, v ? "Email alerts turned on" : "Email alerts turned off")} disabled={busy} />
            <span>
              <b>Send email alerts</b>
              <small>In-app notifications (bell) stay on either way.</small>
            </span>
          </label>
          <div className="recipients" data-testid="alert-recipients">
            <span className="muted small">Recipients:</span>
            {info.recipients.map((r) => {
              const extra = info.alert_recipients.includes(r);
              return (
                <span key={r} className={`mini-chip ${extra ? "" : "ok"}`} title={extra ? "Extra recipient" : "Org admin (always included)"} data-testid={`recipient-${r}`}>
                  {r}
                  {extra && (
                    <button type="button" className="chip-x" onClick={() => save({ alert_recipients: info.alert_recipients.filter((x) => x !== r) }, `${r} removed`)} disabled={busy} aria-label={`Remove ${r}`} data-testid={`recipient-remove-${r}`}>
                      <X size={10} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <form className="ws-inline-form" onSubmit={add}>
            <Input type="email" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a recipient, e.g. it-alerts@contoso.com" className="w-80" data-testid="recipient-input" />
            <button type="submit" className="tb-btn sm" disabled={busy || !draft.trim()} data-testid="recipient-add">
              {busy ? <Loader2 size={13} className="spin" /> : <Plus size={13} />} Add
            </button>
            <button type="button" className="tb-btn sm ghost" onClick={test} disabled={testing || !info.configured} data-testid="alert-test-btn" title="Send a sample alert to your own address">
              {testing ? <Loader2 size={13} className="spin" /> : <Send size={13} />} Send test email
            </button>
          </form>
        </>
      ) : (
        <p className="muted small">Alerts are {info.email_alerts === false ? "off" : "on"} · recipients: {info.recipients.join(", ") || "none"}</p>
      )}
    </section>
  );
}
